import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X as XIcon, Crown, Star } from "lucide-react";
import MarketingNav from "../components/MarketingNav";
import Card from "../components/atoms/Card";
import Chip from "../components/atoms/Chip";
import Button from "../components/atoms/Button";

const PLANS = [
  {
    id: "free",
    name: "Just looking",
    price: "0",
    period: "forever, no card",
    badge: <Chip tone="neutral">Free</Chip>,
    cta: { variant: "ghost", label: "Start free" },
    spine: null,
    accent: false,
    yes: ["First 12 lessons (Beginner)", "1 conversation scenario", "Daily speak practice (5 min)", "Full 102k dictionary"],
    no:  ["AI conversation", "Certificate"],
  },
  {
    id: "app_premium",
    name: "Premium",
    price: "249",
    period: "per month · cancel any time",
    badge: <Chip tone="gold"><Star size={11} strokeWidth={1.8} /> Recommended</Chip>,
    cta: { variant: "primary", label: "Choose Premium" },
    spine: "var(--gold-500)",
    accent: true,
    yes: ["All 50 lessons unlocked", "20 conversation scenarios", "Unlimited speak practice", "AI conversation partner", "Stories, idioms, listening, reading library", "Completion certificate"],
    no:  [],
  },
  {
    id: "app_ielts",
    name: "IELTS Pro",
    price: "449",
    period: "per month · cancel any time",
    badge: <Chip tone="ink"><Crown size={11} strokeWidth={1.8} /> Exam-ready</Chip>,
    cta: { variant: "ink", label: "Choose IELTS Pro" },
    spine: null,
    accent: false,
    yes: ["Everything in Premium", "Full IELTS curriculum", "Mock speaking interviews", "Essay scoring with band feedback", "Test-day countdown plan", "Priority support"],
    no:  [],
  },
];

const COMPARE = [
  ["Lessons",                   "12 of 50",   "All 50",          "All 50 + IELTS"],
  ["Conversation scenarios",    "1",          "20",              "20 + mock interviews"],
  ["Daily speak minutes",       "5",          "Unlimited",       "Unlimited"],
  ["Story / idiom / reading",   "—",          "Full",            "Full"],
  ["AI conversation partner",   "—",          "Yes",             "Yes, with examiner mode"],
  ["Essay scoring",             "—",          "—",               "Band-graded"],
  ["Certificate",               "—",          "On completion",   "On completion"],
  ["Support",                   "Email, 48h", "Email, 24h",      "Priority, same day"],
];

export default function Pricing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [success, setSuccess] = useState(null);
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("currentUser") || "{}"); } catch { return {}; }
  })();
  const currentPlan = (typeof window !== "undefined" && localStorage.getItem("userPlan")) || "free";

  async function handlePayment(planId) {
    if (!user.email) {
      navigate("/login", { state: { from: "/pricing", planId } });
      return;
    }
    setLoading(planId);
    try {
      const orderRes = await fetch("/api/payment/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId, email: user.email, name: user.name }),
      });
      const orderData = await orderRes.json();
      if (!orderData.orderId) { alert(orderData.error || "Failed to create order"); setLoading(null); return; }

      const options = {
        key: orderData.razorpayKey,
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: "SpeakEasy",
        description: orderData.description,
        order_id: orderData.orderId,
        prefill: orderData.prefill,
        theme: { color: "#c89a2c" },
        handler: async function (response) {
          const verifyRes = await fetch("/api/payment/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...response, planId, email: user.email }),
          });
          const verifyData = await verifyRes.json();
          if (verifyData.success) { setSuccess(planId); localStorage.setItem("userPlan", planId); }
          else { alert("Payment verification failed."); }
          setLoading(null);
        },
        modal: { ondismiss: () => setLoading(null) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      alert("Payment failed: " + err.message);
      setLoading(null);
    }
  }

  return (
    <div style={{ background: "var(--paper)", minHeight: "100vh" }}>
      <MarketingNav />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "72px 24px 32px", textAlign: "center" }}>
        <Chip tone="gold">Pricing</Chip>
        <h1 className="font-display" style={{ fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1.05, fontWeight: 400, margin: "16px 0 10px", letterSpacing: "-0.025em" }}>
          Free for as long as it stays useful. <em style={{ color: "var(--gold-500)" }}>Premium when it doesn't.</em>
        </h1>
        <p style={{ color: "var(--fg-2)", fontSize: 17, maxWidth: "56ch", margin: "0 auto", lineHeight: 1.6 }}>
          Twelve lessons free. After that, decide whether SpeakEasy earned the next thirty-eight. Cancel any time, no card on file.
        </p>
      </section>

      {/* Success banner */}
      {success && (
        <div className="max-w-3xl mx-auto px-4" style={{ marginTop: 8, marginBottom: 8 }}>
          <Card paperDeep spineColor="var(--sage)">
            <p style={{ fontFamily: "var(--font-display)", fontSize: 26, lineHeight: 1.1, margin: 0 }}>Payment received.</p>
            <p style={{ color: "var(--fg-2)", fontSize: 14, margin: "6px 0 0" }}>Your plan is active. Enjoy.</p>
          </Card>
        </div>
      )}

      {/* Tiers */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: "32px 24px 48px" }}>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
          {PLANS.map((plan) => {
            const isCurrent = currentPlan === plan.id || success === plan.id;
            return (
              <Card
                key={plan.id}
                spineColor={plan.spine}
                style={{
                  paddingTop: 32, paddingBottom: 28, minHeight: 540,
                  display: "flex", flexDirection: "column",
                  borderColor: plan.accent ? "var(--gold-300)" : "var(--border-2)",
                  background: plan.accent ? "var(--paper)" : "#fff",
                }}
              >
                {plan.badge}
                <h2 className="font-display" style={{ fontSize: 32, fontWeight: 400, margin: "14px 0 6px" }}>{plan.name}</h2>
                <div className="font-display" style={{ fontSize: 56, lineHeight: 1, margin: "8px 0 4px" }}>
                  ₹<em style={{ color: "var(--gold-500)" }}>{plan.price}</em>
                </div>
                <div style={{ color: "var(--fg-3)", fontSize: 14, marginBottom: 18 }}>{plan.period}</div>

                <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 24px", flex: 1 }}>
                  {plan.yes.map((f) => (
                    <li key={f} style={{ display: "flex", gap: 10, padding: "7px 0", fontSize: 14, color: "var(--fg-2)" }}>
                      <Check size={16} strokeWidth={1.8} color="var(--sage-dark)" style={{ flexShrink: 0, marginTop: 3 }} />
                      <span>{f}</span>
                    </li>
                  ))}
                  {plan.no.map((f) => (
                    <li key={f} style={{ display: "flex", gap: 10, padding: "7px 0", fontSize: 14, color: "var(--fg-3)", opacity: 0.7 }}>
                      <XIcon size={16} strokeWidth={1.5} color="var(--ink-300)" style={{ flexShrink: 0, marginTop: 3 }} />
                      <span style={{ textDecoration: "line-through" }}>{f}</span>
                    </li>
                  ))}
                </ul>

                <div style={{ marginTop: "auto" }}>
                  {plan.id === "free" ? (
                    <Link to="/onboarding" style={{ textDecoration: "none", display: "block" }}>
                      <Button variant="ghost" style={{ width: "100%", justifyContent: "center" }}>
                        {isCurrent ? "Current plan" : "Start free"}
                      </Button>
                    </Link>
                  ) : isCurrent ? (
                    <Button variant="ghost" style={{ width: "100%", justifyContent: "center", color: "var(--sage-dark)", borderColor: "#cfe0ce", background: "#eef5ee" }} disabled>
                      <Check size={14} strokeWidth={1.8} /> Active plan
                    </Button>
                  ) : (
                    <Button
                      variant={plan.cta.variant}
                      style={{ width: "100%", justifyContent: "center", opacity: loading === plan.id ? 0.7 : 1 }}
                      onClick={() => handlePayment(plan.id)}
                      disabled={loading === plan.id}
                    >
                      {loading === plan.id ? "Opening checkout…" : plan.cta.label}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>

        <p style={{ textAlign: "center", color: "var(--fg-3)", fontSize: 13, marginTop: 24 }}>
          Building on the Speakeasy API instead?{" "}
          <Link to="/api-docs" style={{ color: "var(--gold-700)", borderBottom: "1px solid currentColor", textDecoration: "none" }}>
            See developer pricing →
          </Link>
        </p>
      </section>

      {/* Compare table */}
      <section style={{ background: "var(--paper-deep)", padding: "64px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display" style={{ fontSize: 36, fontWeight: 400, margin: "0 0 24px" }}>
            What's <em style={{ color: "var(--gold-500)" }}>in</em> each tier
          </h2>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}></th>
                  <th style={th}>Free</th>
                  <th style={th}>Premium</th>
                  <th style={th}>IELTS Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map((row) => (
                  <tr key={row[0]}>
                    <td style={tdName}>{row[0]}</td>
                    <td style={td}>{row[1]}</td>
                    <td style={{ ...td, background: "rgba(250, 240, 197, 0.30)" }}>{row[2]}</td>
                    <td style={td}>{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: 18, fontSize: 13, color: "var(--fg-3)" }}>Powered by Razorpay. UPI, cards, net banking, and wallets accepted.</p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "var(--ink-900)", color: "var(--paper)", padding: "32px 0" }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16, fontSize: 13, color: "rgba(251,246,232,0.55)" }}>
          <span>© {new Date().getFullYear()} SpeakEasy</span>
          <span>Cancel any time. 7-day refund on the first month.</span>
        </div>
      </footer>
    </div>
  );
}

const th = { textAlign: "left", padding: "14px 16px", fontSize: 13, fontWeight: 700, color: "var(--fg-2)", borderBottom: "1px solid var(--border-1)" };
const td = { textAlign: "left", padding: "14px 16px", fontSize: 14, color: "var(--fg-2)", borderBottom: "1px solid var(--border-1)", verticalAlign: "top" };
const tdName = { ...td, color: "var(--fg-1)", fontWeight: 600 };
